import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container, Table, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { FaPrint } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import "../Styling/rawleads.css";

export default function ViewPostedNotes() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelledOnly, setShowCancelledOnly] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchPostedNotes();
  }, []);

  const fetchPostedNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${Base_URL}/CreditNote/GetPostedNotes/${user?.organisationId}`
      );
      setCreditNotes(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("❌ Failed to load posted notes");
    } finally {
      setLoading(false);
    }
  };

  // Filter Cancelled
  const filtered = showCancelledOnly
    ? creditNotes.filter(
        (c) =>
          c.cancelledIndicator === "Yes" ||
          c.cancelledIndicator === "Y" ||
          c.cancelledIndicator === "yes"
      )
    : creditNotes;

  // Split Credit / Debit
  const creditTypeNotes = filtered.filter((c) =>
    c.transactionType?.toLowerCase().includes("credit")
  );

  const debitTypeNotes = filtered.filter((c) =>
    c.transactionType?.toLowerCase().includes("debit")
  );

  const handlePrint = (cn) => {
    const type = cn?.transactionType?.toLowerCase();

    if (type?.includes("credit")) {
      navigate(`/creditNotePrint/${cn.creditNoteId}`);
    } else if (type?.includes("debit")) {
      navigate(`/debitNotePrint/${cn.creditNoteId}`);
    } else {
      toast.error("Unknown transaction type");
    }
  };

  return (
    <div className="main-content expand">
      <Container fluid className="list-container">
        <div className="lead-dashboard container-fluid mt-4">
          
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 className="fw-bold">📋 Posted Credit/Debit Notes</h4>
              <p className="text-muted">
                View all posted credit and debit notes
              </p>
            </div>

            <div>
              <Button variant="outline-primary" className="me-2">
                📊 Export Data
              </Button>

              <Button
                className="btn-assign me-2"
                onClick={fetchPostedNotes}
              >
                🔄 Refresh
              </Button>

              <Form.Check
                type="checkbox"
                label="Show Cancelled Only"
                className="d-inline-block ms-2"
                checked={showCancelledOnly}
                onChange={(e) =>
                  setShowCancelledOnly(e.target.checked)
                }
                style={{ transform: "scale(1.1)", cursor: "pointer" }}
              />
            </div>
          </div>

          {/* STATS */}
          <div className="d-flex gap-3 mb-4">
            <div className="stat-card">
              <h5 className="text-primary">{creditNotes.length}</h5>
              <p className="text-muted">Total Posted</p>
            </div>

            <div className="stat-card">
              <h5 className="text-primary">
                {
                  creditNotes.filter((c) =>
                    c.transactionType
                      ?.toLowerCase()
                      .includes("credit")
                  ).length
                }
              </h5>
              <p className="text-muted">Credit Notes</p>
            </div>

            <div className="stat-card">
              <h5 className="text-primary">
                {
                  creditNotes.filter((c) =>
                    c.transactionType
                      ?.toLowerCase()
                      .includes("debit")
                  ).length
                }
              </h5>
              <p className="text-muted">Debit Notes</p>
            </div>

            <div className="stat-card">
              <h5 className="text-primary">
                {
                  creditNotes.filter((c) =>
                    moment(c.creditNoteDate).isSame(
                      moment(),
                      "month"
                    )
                  ).length
                }
              </h5>
              <p className="text-muted">This Month</p>
            </div>
          </div>
        </div>

        {/* TABLE SECTION */}
        <div className="raw-leads-table">
          {loading ? (
            <p className="no-data text-center py-4">
              Loading posted notes...
            </p>
          ) : (
            <>
              {/* CREDIT NOTES */}
              <h5 className="mb-2">Credit Notes</h5>
              {creditTypeNotes.length > 0 ? (
                <Table bordered hover responsive className="data-table mb-4">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>CreditNote ID</th>
                      <th>Company Code</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Customer Code</th>
                      <th>Invoice Number</th>
                      <th>Date</th>
                      <th>Cancelled</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditTypeNotes.map((cn, idx) => (
                      <tr key={cn.creditNoteId}>
                        <td>{idx + 1}</td>
                        <td>{cn.creditNoteId}</td>
                        <td>{cn.companyCode}</td>
                        <td>{cn.transactionType}</td>
                        <td>{cn.category}</td>
                        <td>{cn.customerCode || "-"}</td>
                        <td>{cn.invoiceNumber || "-"}</td>
                        <td>
                          {cn.creditNoteDate
                            ? moment(cn.creditNoteDate).format("L")
                            : "-"}
                        </td>
                        <td>{cn.cancelledIndicator || "-"}</td>
                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handlePrint(cn)}
                          >
                            <FaPrint />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="no-data text-center py-2">
                  No credit notes found
                </p>
              )}

              {/* DEBIT NOTES */}
              <h5 className="mb-2 mt-3">Debit Notes</h5>
              {debitTypeNotes.length > 0 ? (
                <Table bordered hover responsive className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>CreditNote ID</th>
                      <th>Company Code</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Customer Code</th>
                      <th>Invoice Number</th>
                      <th>Date</th>
                      <th>Cancelled</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debitTypeNotes.map((cn, idx) => (
                      <tr key={cn.creditNoteId}>
                        <td>{idx + 1}</td>
                        <td>{cn.creditNoteId}</td>
                        <td>{cn.companyCode}</td>
                        <td>{cn.transactionType}</td>
                        <td>{cn.category}</td>
                        <td>{cn.customerCode || "-"}</td>
                        <td>{cn.invoiceNumber || "-"}</td>
                        <td>
                          {cn.creditNoteDate
                            ? moment(cn.creditNoteDate).format("L")
                            : "-"}
                        </td>
                        <td>{cn.cancelledIndicator || "-"}</td>
                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handlePrint(cn)}
                          >
                            <FaPrint />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="no-data text-center py-2">
                  No debit notes found
                </p>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
}